import type { IssueLabelItem, LabelPrintKind, LabelReconciliation, LabelUsage, ProductLabel } from '@/api/productLabelsApi'
import type { ProductLabelSelections } from '@/lib/digitrax'

/**
 * Pure rules for printed product labels (z8nrz7dt48), kept out of the dialogs
 * so they are unit-tested (vitest here runs `.ts` only).
 */

/** What each label signature means, as the audit trail records it (21 CFR Part 11). */
export const LABEL_SIGNATURE_MEANINGS = {
  prePrint: 'I verified this label against the product record before printing',
  postPrint: 'I verified the printed label against the product record',
  discarded: 'I am discarding this label',
  unused: 'I am returning this label unused',
} as const

/** One label to issue, from the product and the selections it prints with. */
export function issueItemFrom(
  productId: string,
  selections: ProductLabelSelections | undefined,
  printKind: LabelPrintKind,
): IssueLabelItem {
  return {
    productId,
    labelType: selections?.labelType ?? 'Standard',
    bloodType: selections?.bloodType || undefined,
    specialMessage: selections?.specialMessage || undefined,
    intendedUse: selections?.intendedUse || undefined,
    donationType: selections?.donationType || undefined,
    freeText: (selections?.freeText ?? []).filter((line): line is string => !!line?.trim()),
    printKind,
  }
}

export const USAGE_LABEL: Record<LabelUsage, string> = {
  pending: 'Not verified',
  applied: 'Applied',
  discarded: 'Discarded',
  unused: 'Returned unused',
}

export const PRINT_KIND_LABEL: Record<LabelPrintKind, string> = {
  original: 'New label',
  reprint: 'Reprint, no changes',
  relabel: 'Reprint with changes',
}

/** "Nathan · 24 Sep 2026, 10:36", for a signature cell. */
export function signedText(name: string | null | undefined, at: string | null | undefined, locale?: string): string {
  if (!at) return ''
  const when = new Date(at).toLocaleString(locale, { dateStyle: 'medium', timeStyle: 'short' })
  return `${name || 'Unknown'} · ${when}`
}

/**
 * The Post-print verification cell, as the legacy label screen shows it: who
 * verified and when, "Pending" while it waits, "Not required" once the label is
 * discarded or returned — it will never be on a bag.
 */
export function postPrintText(label: Pick<ProductLabel, 'usage' | 'postVerifiedByName' | 'postVerifiedBy' | 'postVerifiedAt'>, locale?: string): string {
  if (label.usage === 'discarded' || label.usage === 'unused') return 'Not required'
  if (!label.postVerifiedAt) return 'Pending'
  return signedText(label.postVerifiedByName ?? label.postVerifiedBy, label.postVerifiedAt, locale)
}

/** Which actions a label offers: verify while pending; discard until final; return while pending. */
export function labelActions(usage: LabelUsage): { verify: boolean; discard: boolean; returnUnused: boolean } {
  return {
    verify: usage === 'pending',
    discard: usage === 'pending' || usage === 'applied',
    returnUnused: usage === 'pending',
  }
}

/** issued = applied + discarded + unused, and what is still unaccounted for. */
export function reconciliationLine(r: Pick<LabelReconciliation, 'issued' | 'applied' | 'discarded' | 'unused' | 'pending'>): string {
  const base = `${r.issued} issued = ${r.applied} applied + ${r.discarded} discarded + ${r.unused} unused`
  return r.pending ? `${base}; ${r.pending} not accounted for` : base
}

/**
 * The counts a reviewer saw, as one key: a reconciliation shown before Complete
 * Form (AC4) is shown again if a label moves before the form actually completes.
 */
export function labelCountsKey(r: Pick<LabelReconciliation, 'issued' | 'applied' | 'discarded' | 'unused' | 'pending'>): string {
  return [r.issued, r.applied, r.discarded, r.unused, r.pending].join('/')
}

/**
 * Whether Complete Form shows the label result before it signs (AC4): whenever
 * the form printed labels and the person completing it has not seen these
 * exact counts. A form with no labels completes as before (AC11).
 */
export function needsLabelReview(r: LabelReconciliation | undefined, seenKey: string | null | undefined): boolean {
  return !!r && r.issued > 0 && seenKey !== labelCountsKey(r)
}
